import { el, inject } from "@dhx/ts-common/dom";
import { Combobox, ComboboxEvents } from "@dhx/ts-combobox";
import { DataEvents } from "@dhx/ts-data";
import { IEventSystem } from "@dhx/ts-common/events";
import { fixedRowContent, GridEvents, ICol, IContentList, IRendererConfig } from "../types";
import { toFormat } from "../helpers/data";
import { isIE, isSafari } from "@dhx/ts-common/html";
import { Id } from "@dhx/ts-common/types";
import { uid } from "@dhx/ts-common/core";

let inputDelay: any;

function onInput(
	eventSystem: IEventSystem<GridEvents>,
	colId: Id,
	filter: fixedRowContent,
	filterObj: IContentList,
	e: any
) {
	const inputHandler = () => {
		const val =
			isIE() || isSafari() ? e.target.value : e.path ? e.path[0].value : e.explicitOriginalTarget.value;
		filterObj.value[colId] = val;
		eventSystem.fire(GridEvents.filterChange, [val, colId, filter]);
	};
	if (filter === "selectFilter") {
		inputHandler();
		return;
	}
	if (inputDelay) {
		clearTimeout(inputDelay);
	}
	inputDelay = setTimeout(inputHandler, 500);
}

function applyMathMethod(column: ICol, config: IRendererConfig, method: any, validate?: any): any {
	if (!column || !config || !method) {
		return;
	}
	const id = column.id;
	const columnData = validate
		? validate(id, config.data)
		: config.data.reduce((items, item) => {
				if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
					items.push(parseFloat(item[id]));
				}
				return items;
		  }, []);
	// [todo] move to treegrid
	let roots = columnData;
	if (config.type === "tree") {
		roots = config.data.reduce((total, item) => {
			if (item.$level === 0) {
				if (item[id] !== undefined && item[id] !== "" && !isNaN(item[id])) {
					total.push(parseFloat(item[id]) || 0);
				} else {
					let value = 0;
					config.datacollection.eachChild(item.id, cell => {
						if (!config.datacollection.haveItems(cell.id)) {
							value += parseFloat(cell[id]);
						}
					});
					total.push(value);
				}
			}
			return total;
		}, []);
	}
	return method(columnData, roots);
}

export function getContent(): IContentList {
	const getUniqueData = column => {
		let uniqueData = column.$uniqueData;
		if (
			(column.editorType === "combobox" ||
				column.editorType === "select" ||
				column.editorType === "multiselect") &&
			column.options
		) {
			uniqueData = uniqueData.map(item => {
				const foundItem = column.options.find(option =>
					typeof option === "string" ? item === option : item === option.id
				);
				return foundItem && typeof foundItem !== "string" ? foundItem.value : item;
			});
		}

		return column.type !== "string"
			? Array.from(new Set(uniqueData.map(val => toFormat(val, column.type, column.format))))
			: uniqueData;
	};

	return {
		inputFilter: {
			element: {},
			toHtml(column, config) {
				const id = uid();
				const colId = column.id.toString();
				this.element[colId] = el(
					"div.dhx_grid-filter__label.dxi.dxi-magnify",
					{ "aria-label": "Type to search", _ref: `${column.id}_filter` },
					[
						el(
							"label",
							{
								style: {
									display: "none",
								},
								"aria-label": "Type to search",
								for: id,
							},
							"Type to search"
						),
						el("input", {
							type: "text",
							class: "dhx_input dhx_grid-filter",
							oninput: [onInput, config.events, column.id, "inputFilter", this],
							_key: column.id,
							id: id,
							value: this.value[column.id] || "",
						}),
					]
				);
				return this.element[colId];
			},
			match: ({ val, match, col }) => {
				const value = toFormat(val, col.type, col.format);
				let res = "";
				for (let i = 0; i < match.length; i++) {
					const char = match.charCodeAt(i);
					if ((char > 32 && char < 48) || char === 63 || (char > 90 && char < 95) || char === 124) {
						res += "\\" + match[i];
					} else {
						res += match[i];
					}
				}
				return new RegExp(`${res}`, "i").test(value);
			},
			value: {},
		},
		selectFilter: {
			element: {},
			toHtml(column, config) {
				const colId = column.id.toString();
				const uniqueData = getUniqueData(column);
				this.element[colId] = el(
					"label.dhx_grid-filter__label.dxi.dxi-menu-down",
					{ _ref: `${column.id}_filter` },
					[
						el(
							"select.dxi.dxi-menu-down",
							{
								type: "text",
								class: "dhx_input dhx_grid-filter dhx_grid-filter--select",
								onchange: [onInput, config.events, column.id, "selectFilter", this],
								_key: column.id,
							},
							[
								el("option", { value: "" }, ""),
								uniqueData.map(val => {
									val = val ?? "";
									return (
										val !== "" &&
										el(
											"option",
											{
												value: val,
												selected: this.value[column.id] === val.toString(),
											},
											val
										)
									);
								}),
							]
						),
					]
				);
				return this.element[colId];
			},
			match: ({ val, match, col }) => {
				const value = toFormat(val, col.type, col.format);
				return match ? (value || typeof value === "boolean") && value.toString() == match : true;
			},
			value: {},
		},
		comboFilter: {
			element: {},
			toHtml(column, config) {
				let combo;
				const colId = column.id.toString();
				if (!this.element[colId] && config.events) {
					const uniqueData = getUniqueData(column);
					const conf = column.header.filter(item => item.filterConfig !== undefined)[0];
					if (conf && conf.filterConfig) {
						combo = new Combobox(null, Object.assign({}, conf.filterConfig));
					} else {
						combo = new Combobox(null, {});
					}
					combo.data.parse(uniqueData.map(value => ({ value })));
					config.events.on(DataEvents.load, () => {
						combo.data.parse(column.$uniqueData.map(value => ({ value })));
					});
					this.element[colId] = combo;
					combo.events.on("change", id => {
						if (id) {
							let value;
							const haveIds = Array.isArray(id)
								? id.find(item => combo.data.getItem(item))
								: combo.data.getItem(id);
							if (haveIds) {
								value = combo.config.multiselection
									? combo.list.selection.getItem().map(item => {
											if (item && combo.data.getItem(item.id)) {
												return item.value;
											}
									  })
									: combo.list.selection.getItem().value;
								config.events.fire(GridEvents.filterChange, [value, colId, "comboFilter"]);
							} else {
								config.events.fire(GridEvents.filterChange, ["", colId, "comboFilter"]);
							}
						}
					});
					config.events.on(DataEvents.change, (id, status) => {
						if (status === "add" || status === "update" || status === "remove") {
							const uniqueData = getUniqueData(column);
							combo.data.parse(uniqueData.map(value => ({ value })));
							combo.events.fire(ComboboxEvents.change, [combo.list.selection.getItem()]);
						}
					});
					config.events.on(DataEvents.removeAll, () => {
						combo.data.parse(column.$uniqueData.map(value => ({ value })));
						combo.events.fire(ComboboxEvents.change, [combo.list.selection.getItem()]);
					});
					combo.popup.events.on("afterHide", () => {
						if (
							!combo.list.selection.getItem() ||
							(combo.config.multiselection && !combo.list.selection.getItem().length)
						) {
							combo.clear();
							config.events.fire(GridEvents.filterChange, ["", colId, "comboFilter"]);
						}
					});
				} else {
					combo = this.element[column.id] || new Combobox(null, {});
				}
				return inject(combo.getRootView());
			},
			match: ({ val, match = "", multi = false, col }) => {
				const value = toFormat(val, col.type, col.format);
				if (Array.isArray(match)) {
					let result;
					for (let i = 0; i < match.length; i++) {
						result = !multi
							? match[i] === value
							: !!value.split(", ").find(item => item === match[i]);
						if (result) break;
					}
					return match && match.length ? result : true;
				} else {
					return match !== "" ? value === match : true;
				}
			},
			destroy: () => {
				if (this.content && this.content.comboFilter.element) {
					const comboFilters = this.content.comboFilter.element;
					for (const combo in comboFilters) {
						comboFilters[combo].destructor();
						delete comboFilters[combo];
					}
				}
			},
			value: {},
		},
		sum: {
			calculate: (col, roots) => roots.reduce((sum, c) => (sum += parseFloat(c) || 0), 0),
			toHtml(column, config) {
				const value = applyMathMethod(column, config, this.calculate);
				if (column.format || column.type === "percent") {
					return toFormat(value, column.type, column.format);
				} else {
					return value ? value.toFixed(3) : null;
				}
			},
		},
		avg: {
			calculate: (col, roots) =>
				col.length ? roots.reduce((sum, c) => (sum += c), 0) / col.length : null,
			toHtml(column, config) {
				const value = applyMathMethod(column, config, this.calculate);
				if (column.format || column.type === "percent") {
					return toFormat(value, column.type, column.format);
				} else {
					return value ? value.toFixed(3) : null;
				}
			},
		},
		min: {
			calculate: col => (col.length ? Math.min(...col) : null),
			toHtml(column, config) {
				const value = applyMathMethod(column, config, this.calculate);
				if (column.format || column.type === "percent") {
					return toFormat(value, column.type, column.format);
				} else {
					return value ? value.toFixed(3) : null;
				}
			},
		},
		max: {
			calculate: col => (col.length ? Math.max(...col) : null),
			toHtml(column, config) {
				const value = applyMathMethod(column, config, this.calculate);
				if (column.format || column.type === "percent") {
					return toFormat(value, column.type, column.format);
				} else {
					return value ? value.toFixed(3) : null;
				}
			},
		},
		count: {
			calculate: (_col, roots) => {
				// [todo]
				return roots.length;
			},
			validate: (colId: Id, data: any[]) => {
				return data.reduce((items, item) => {
					if (item[colId] !== undefined && item[colId] !== "") {
						if (isNaN(item)) {
							items.push(1);
						} else {
							items.push(item);
						}
					}
					return items;
				}, []);
			},
			toHtml(column, config) {
				return applyMathMethod(column, config, this.calculate, this.validate);
			},
		},
	};
}
